import React, { useState, useEffect } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import { Actions as ApiCallActions } from '../../redux/apiCall/reducers'
import general from '../../utils/general'
import Input from '../../components/input'
import Select from '../../components/select'
import Editor from '../../components/editor'
import FileUpload from '../../components/file-upload'
import { ValidationPatterns, ValidationMessages } from '../../utils/validation'
import useI18n from '../../i18n/useI18n'
import EventBus from '../../infrastructure/event-bus/event-bus'
import { UniversityTypeOptions } from '../../utils/enums'

const UniversityFormPage = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { id } = useParams()
  const { t } = useI18n()
  
  const isEditMode = !!id
  const [activeTab, setActiveTab] = useState('single')
  const [bulkFile, setBulkFile] = useState(null)
  const [bulkUploadStatus, setBulkUploadStatus] = useState('idle')
  const [showFieldGuide, setShowFieldGuide] = useState(false)
  const [templateDownloading, setTemplateDownloading] = useState(false)
  const [bulkUploaderKey, setBulkUploaderKey] = useState(0)
  
  const [formData, setFormData] = useState({
    name: '',
    nameEn: '',
    nameRu: '',
    logoUrl: '',
    websiteUrl: '',
    description: '',
    location: '',
    cityId: '',
    province: '',
    country: 'Turkey',
    latitude: '',
    longitude: '',
    universityType: '',
    isActive: true
  })

  const [validationErrors, setValidationErrors] = useState({})
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState('')
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (isEditMode && id) {
      fetchUniversityData()
    }
  }, [id, isEditMode])

  const fetchUniversityData = () => {
    if (!id) return
    
    setLoading(true)
    dispatch(
      ApiCallActions.Get({
        url: `universities/${id}`,
        showAlertOnError: true,
        showLoading: true,
        onSuccess: ({ data }) => {
          const universityData = data?.data || data
          if (universityData) {
            setFormData({
              name: universityData.name || '',
              nameEn: universityData.nameEn || '',
              nameRu: universityData.nameRu || '',
              logoUrl: universityData.logoUrl || '',
              websiteUrl: universityData.websiteUrl || '',
              description: universityData.description || '',
              location: universityData.location || '',
              cityId:
                (universityData.cityId !== undefined && universityData.cityId !== null
                  ? universityData.cityId.toString()
                  : universityData.city?.id !== undefined && universityData.city?.id !== null
                  ? universityData.city.id.toString()
                  : ''),
              province: universityData.province || '',
              country: universityData.country || 'Turkey',
              latitude: universityData.latitude || '',
              longitude: universityData.longitude || '',
              universityType: universityData.universityType || universityData.university_type || '',
              isActive: universityData.isActive !== undefined ? universityData.isActive : true
            })
          }
          setLoading(false)
        },
        onError: ({ errorMessage }) => {
          general.notificationError(errorMessage || t('UNIVERSITIES_ERR_LOAD_FAILED'))
          setLoading(false)
          navigate('/universities')
        }
      })
    )
  }

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }))
    setSubmitError('')
  }

  const handleValidationChange = (fieldName, isValid, errorMessage) => {
    setValidationErrors(prev => ({
      ...prev,
      [fieldName]: isValid ? '' : errorMessage
    }))
  }

  const handleCheckboxChange = (e) => {
    const { name, checked } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: checked
    }))
    setSubmitError('')
  }

  const handleTabChange = (tabKey) => {
    setActiveTab(tabKey)
  }

  const formatFileSize = (bytes) => {
    if (!bytes && bytes !== 0) {
      return ''
    }
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    const value = bytes / Math.pow(k, i)
    return `${Math.round(value * 100) / 100} ${sizes[i]}`
  }

  const handleTemplateDownload = () => {
    if (templateDownloading) {
      return
    }

    setTemplateDownloading(true)

    dispatch(
      ApiCallActions.Get({
        url: 'universities/excel/template',
        axiosOptions: {
          responseType: 'blob'
        },
        showAlertOnError: true,
        onSuccess: ({ data }) => {
          const blobSource = data?.data || data

          if (!blobSource) {
            general.notificationError(t('UNIVERSITIES_BULK_UPLOAD_TEMPLATE_ERROR'))
            return
          }

          const blob =
            blobSource instanceof Blob
              ? blobSource
              : new Blob([blobSource], {
                  type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
                })

          const downloadUrl = window.URL.createObjectURL(blob)
          const link = document.createElement('a')
          link.href = downloadUrl
          link.download = 'universiteler-sablon.xlsx'
          document.body.appendChild(link)
          link.click()
          document.body.removeChild(link)
          window.URL.revokeObjectURL(downloadUrl)

          general.messageSuccess(t('UNIVERSITIES_BULK_UPLOAD_TEMPLATE_READY'))
        },
        onError: ({ errorMessage }) => {
          general.notificationError(errorMessage || t('UNIVERSITIES_BULK_UPLOAD_TEMPLATE_ERROR'))
        },
        callback: () => {
          setTemplateDownloading(false)
        }
      })
    )
  }

  const handleBulkFileSelect = (file) => {
    setBulkFile(file)
    setBulkUploadStatus(file ? 'ready' : 'idle')
  }

  const handleBulkUpload = () => {
    if (!bulkFile) {
      general.notificationWarning(t('UNIVERSITIES_BULK_UPLOAD_NO_FILE'))
      return
    }

    setBulkUploadStatus('processing')

    const formData = new FormData()
    formData.append('file', bulkFile)

    dispatch(
      ApiCallActions.Post({
        url: 'universities/excel/upload',
        showAlertOnError: false,
        axiosOptions: {
          data: formData
        },
        onSuccess: () => {
          setBulkFile(null)
          setBulkUploaderKey(prev => prev + 1)
          setBulkUploadStatus('idle')

          general.messageSuccess(t('UNIVERSITIES_BULK_UPLOAD_UPLOAD_SUCCESS'))
          navigate('/universities')
        },
        onError: ({ errorMessage }) => {
          const message = errorMessage || t('UNIVERSITIES_BULK_UPLOAD_UPLOAD_ERROR')
          setBulkUploadStatus(bulkFile ? 'ready' : 'idle')
          general.notificationError(message)
        }
      })
    )
  }

  const getBulkStatusMessage = () => {
    switch (bulkUploadStatus) {
      case 'ready':
        return t('UNIVERSITIES_BULK_UPLOAD_STATUS_READY')
      case 'processing':
        return t('UNIVERSITIES_BULK_UPLOAD_PROCESSING')
      default:
        return t('UNIVERSITIES_BULK_UPLOAD_STATUS_IDLE')
    }
  }

  const isFormValid = () => {
    if (!formData.name || formData.name.trim() === '') {
      return false
    }
    return Object.values(validationErrors).every(error => error === '')
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    
    if (!formData.name || formData.name.trim() === '') {
      setValidationErrors(prev => ({
        ...prev,
        name: ValidationMessages.REQUIRED
      }))
      return
    }

    if (!isFormValid()) {
      return
    }

    setIsSubmitting(true)
    setSubmitError('')

    const submitData = {
      name: formData.name.trim()
    }

    general.addOptionalFields(submitData, formData, {
      string: ['nameEn', 'nameRu', 'logoUrl', 'websiteUrl', 'description', 'location', 'province', 'country'],
      number: ['latitude', 'longitude'],
      boolean: ['isActive']
    })

    if (formData.cityId) {
      submitData.cityId = isNaN(Number(formData.cityId)) ? formData.cityId : Number(formData.cityId)
    }

    // University type camelCase gönderimi zorunlu olsun
    const normalizedUniType = formData.universityType?.trim()
    if (normalizedUniType) {
      submitData.universityType = normalizedUniType
    }

    if (isEditMode) {
      dispatch(
        ApiCallActions.Put({
          url: `universities/${id}`,
          data: submitData,
          showAlertOnError: true,
          showLoading: true,
          onSuccess: ({ data }) => {
            setIsSubmitting(false)
            
            const apiEntityData = data?.data || data
            const mergedEntityData = {
              id: id,
              ...formData,
              ...submitData,
              ...apiEntityData
            }
            
            EventBus.sc.onEntityUpdated({
              entityType: 'university',
              entityId: id,
              entityData: mergedEntityData
            })
            
            general.messageSuccess(t('UNIVERSITIES_MSG_UPDATED'))
            navigate('/universities')
          },
          onError: ({ errorMessage }) => {
            setIsSubmitting(false)
            setSubmitError(errorMessage || t('UNIVERSITIES_ERR_UPDATE_FAILED'))
          }
        })
      )
    } else {
      dispatch(
        ApiCallActions.Post({
          url: "universities",
          data: submitData,
          showAlertOnError: true,
          showLoading: true,
          onSuccess: ({ data }) => {
            setIsSubmitting(false)
            
            const entityData = data?.data || data
            
            if (entityData?.id) {
              EventBus.sc.onEntityCreated({
                entityType: 'university',
                entityId: entityData.id,
                entityData: entityData
              })
            }
            
            general.messageSuccess(t('UNIVERSITIES_MSG_CREATED'))
            navigate('/universities')
          },
          onError: ({ errorMessage }) => {
            setIsSubmitting(false)
            setSubmitError(errorMessage || t('UNIVERSITIES_ERR_CREATE_FAILED'))
          }
        })
      )
    }
  }

  const renderSingleForm = () => (
    <>
      {submitError && (
        <div className="alert alert-danger" role="alert">
          {submitError}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="row">
          <div className="col-md-4">
            <div className="form-group">
              <label>{t('UNIVERSITIES_FORM_FIELD_NAME_TR')} <span className="text-danger">*</span></label>
              <Input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder={t('UNIVERSITIES_FORM_FIELD_NAME_TR_PLACEHOLDER')}
                required
                validationRegex={ValidationPatterns.MIN_2_CHARS}
                errorMessage={ValidationMessages.MIN_2_CHARS}
                onValidationChange={handleValidationChange}
              />
            </div>
          </div>
          <div className="col-md-4">
            <div className="form-group">
              <label>{t('UNIVERSITIES_FORM_FIELD_NAME_EN')}</label>
              <Input
                type="text"
                name="nameEn"
                value={formData.nameEn}
                onChange={handleChange}
                placeholder={t('UNIVERSITIES_FORM_FIELD_NAME_EN_PLACEHOLDER')}
                validationRegex={formData.nameEn ? ValidationPatterns.NAME_EN : null}
                errorMessage={ValidationMessages.NAME_EN}
                onValidationChange={handleValidationChange}
                validateOnBlur={true}
              />
            </div>
          </div>
          <div className="col-md-4">
            <div className="form-group">
              <label>{t('UNIVERSITIES_FORM_FIELD_NAME_RU')}</label>
              <Input
                type="text"
                name="nameRu"
                value={formData.nameRu}
                onChange={handleChange}
                placeholder={t('UNIVERSITIES_FORM_FIELD_NAME_RU_PLACEHOLDER')}
                onValidationChange={handleValidationChange}
                validateOnBlur={true}
              />
            </div>
          </div>
        </div>

        <div className="row">
          <div className="col-md-12">
            <div className="form-group">
              <label>{t('UNIVERSITIES_FORM_FIELD_DESCRIPTION')}</label>
              <Editor
                value={formData.description}
                onChange={(value) => setFormData(prev => ({ ...prev, description: value }))}
                placeholder={t('UNIVERSITIES_FORM_FIELD_DESCRIPTION_PLACEHOLDER')}
                minHeight={150}
              />
            </div>
          </div>
        </div>

        <div className="row">
          <div className="col-md-6">
            <div className="form-group">
              <label>{t('UNIVERSITIES_FORM_FIELD_LOGO_URL')}</label>
              <Input
                type="url"
                name="logoUrl"
                value={formData.logoUrl}
                onChange={handleChange}
                placeholder={t('UNIVERSITIES_FORM_FIELD_LOGO_URL_PLACEHOLDER')}
                validationRegex={formData.logoUrl ? ValidationPatterns.URL : null}
                errorMessage={ValidationMessages.URL}
                onValidationChange={handleValidationChange}
                validateOnBlur={true}
              />
            </div>
          </div>
          <div className="col-md-6">
            <div className="form-group">
              <label>{t('UNIVERSITIES_FORM_FIELD_WEBSITE_URL')}</label>
              <Input
                type="url"
                name="websiteUrl"
                value={formData.websiteUrl}
                onChange={handleChange}
                placeholder={t('UNIVERSITIES_FORM_FIELD_WEBSITE_URL_PLACEHOLDER')}
                validationRegex={formData.websiteUrl ? ValidationPatterns.URL : null}
                errorMessage={ValidationMessages.URL}
                onValidationChange={handleValidationChange}
                validateOnBlur={true}
              />
            </div>
          </div>
        </div>

        <div className="row">
          <div className="col-md-6">
            <div className="form-group">
              <label>{t('UNIVERSITIES_FORM_FIELD_COUNTRY')}</label>
              <Input
                type="text"
                name="country"
                value={formData.country}
                onChange={handleChange}
                placeholder={t('UNIVERSITIES_FORM_FIELD_COUNTRY_PLACEHOLDER')}
                validationRegex={formData.country ? ValidationPatterns.CITY_PROVINCE : null}
                errorMessage={ValidationMessages.CITY_PROVINCE}
                onValidationChange={handleValidationChange}
                validateOnBlur={true}
              />
            </div>
          </div>
          <div className="col-md-6">
            <div className="form-group">
              <label>{t('UNIVERSITIES_FORM_FIELD_CITY')}</label>
              <Select
                name="cityId"
                value={formData.cityId}
                onChange={handleChange}
                placeholder={t('UNIVERSITIES_FORM_FIELD_CITY_PLACEHOLDER')}
                fetchUrl="cities"
                mapOption={(item) => {
                  if (!item) return null
                  const id = item.id ?? item.value
                  const label = item.cityName || item.name || item.label
                  if (id === undefined || id === null || !label) {
                    return null
                  }
                  return {
                    value: id.toString(),
                    label: label.toString()
                  }
                }}
              />
            </div>
          </div>
        </div>

        <div className="row">
          <div className="col-md-6">
            <div className="form-group">
              <label>{t('UNIVERSITIES_FORM_FIELD_TYPE')}</label>
              <Select
                name="universityType"
                value={formData.universityType}
                onChange={handleChange}
                placeholder={t('UNIVERSITIES_FORM_FIELD_TYPE_PLACEHOLDER')}
                options={UniversityTypeOptions.map(opt => ({
                  ...opt,
                  label: t(opt.label)
                }))}
              />
            </div>
          </div>
        </div>

        <div className="row">
          <div className="col-md-6">
            <div className="form-group">
              <label>{t('UNIVERSITIES_FORM_FIELD_PROVINCE')}</label>
              <Input
                type="text"
                name="province"
                value={formData.province}
                onChange={handleChange}
                placeholder={t('UNIVERSITIES_FORM_FIELD_PROVINCE_PLACEHOLDER')}
                validationRegex={formData.province ? ValidationPatterns.CITY_PROVINCE : null}
                errorMessage={ValidationMessages.CITY_PROVINCE}
                onValidationChange={handleValidationChange}
                validateOnBlur={true}
              />
            </div>
          </div>
          <div className="col-md-6">
            <div className="form-group">
              <label>{t('UNIVERSITIES_FORM_FIELD_LOCATION')}</label>
              <Input
                type="text"
                name="location"
                value={formData.location}
                onChange={handleChange}
                placeholder={t('UNIVERSITIES_FORM_FIELD_LOCATION_PLACEHOLDER')}
                validationRegex={formData.location ? ValidationPatterns.MIN_2_CHARS : null}
                errorMessage={ValidationMessages.MIN_2_CHARS}
                onValidationChange={handleValidationChange}
                validateOnBlur={true}
              />
            </div>
          </div>
        </div>

        <div className="row">
          <div className="col-md-6">
            <div className="form-group">
              <label>{t('UNIVERSITIES_FORM_FIELD_LATITUDE')}</label>
              <Input
                type="number"
                name="latitude"
                value={formData.latitude}
                onChange={handleChange}
                placeholder={t('UNIVERSITIES_FORM_FIELD_LATITUDE_PLACEHOLDER')}
                step="any"
                min="-90"
                max="90"
                validationRegex={formData.latitude ? ValidationPatterns.LATITUDE : null}
                errorMessage={ValidationMessages.LATITUDE}
                onValidationChange={handleValidationChange}
                validateOnBlur={true}
              />
            </div>
          </div>
          <div className="col-md-6">
            <div className="form-group">
              <label>{t('UNIVERSITIES_FORM_FIELD_LONGITUDE')}</label>
              <Input
                type="number"
                name="longitude"
                value={formData.longitude}
                onChange={handleChange}
                placeholder={t('UNIVERSITIES_FORM_FIELD_LONGITUDE_PLACEHOLDER')}
                step="any"
                min="-180"
                max="180"
                validationRegex={formData.longitude ? ValidationPatterns.LONGITUDE : null}
                errorMessage={ValidationMessages.LONGITUDE}
                onValidationChange={handleValidationChange}
                validateOnBlur={true}
              />
            </div>
          </div>
        </div>

        <div className="row">
          <div className="col-md-12">
            <div className="form-group">
              <div className="checkbox">
                <input
                  id="isActive-checkbox"
                  type="checkbox"
                  name="isActive"
                  checked={formData.isActive}
                  onChange={handleCheckboxChange}
                />
                <label htmlFor="isActive-checkbox">{t('UNIVERSITIES_FORM_FIELD_IS_ACTIVE')}</label>
              </div>
            </div>
          </div>
        </div>

        <div className="row">
          <div className="col-md-12">
            <button 
              type="button" 
              className="btn btn-secondary m-r-10"
              onClick={() => navigate('/universities')}
              disabled={isSubmitting}
            >
              {t('UNIVERSITIES_FORM_BUTTON_CANCEL')}
            </button>
            <button 
              type="submit" 
              className="btn btn-primary"
              disabled={isSubmitting}
            >
              {isSubmitting
                ? (isEditMode ? t('UNIVERSITIES_FORM_BUTTON_UPDATING') : t('UNIVERSITIES_FORM_BUTTON_SAVING'))
                : (isEditMode ? t('UNIVERSITIES_FORM_BUTTON_UPDATE') : t('UNIVERSITIES_FORM_BUTTON_SAVE'))}
            </button>
          </div>
        </div>
      </form>
    </>
  )

  const renderBulkUpload = () => {
    const steps = [
      t('UNIVERSITIES_BULK_UPLOAD_STEP_DOWNLOAD'),
      t('UNIVERSITIES_BULK_UPLOAD_STEP_FILL'),
      // t('UNIVERSITIES_BULK_UPLOAD_STEP_UPLOAD')
    ] 
   
    return (
      <div className="bulk-upload-pane">
        <div className="row">
          <div className="col-lg-7 col-md-12">
            <h5 className="m-b-10">{t('UNIVERSITIES_BULK_UPLOAD_TITLE')}</h5>
            <p className="text-muted">{t('UNIVERSITIES_BULK_UPLOAD_DESCRIPTION')}</p>
            {steps.map((step, index) => (
                <div key={step} className="m-b-5">
                  <strong style={{ display: 'inline-block', width: '20px' }}>{index + 1}.</strong> {step}
                </div>
              ))}
            <div className="m-t-20">
              <button
                type="button"
                className="btn btn-raised btn-round btn-primary m-r-10"
                style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}
                onClick={handleTemplateDownload}
                disabled={templateDownloading}
              >
                {templateDownloading ? (
                  <>
                    <i className="zmdi zmdi-refresh zmdi-hc-spin m-r-5"></i>
                    {t('UNIVERSITIES_BULK_UPLOAD_TEMPLATE_DOWNLOADING')}
                  </>
                ) : (
                  <>
                    <i className="zmdi zmdi-download m-r-5"></i>
                    {t('UNIVERSITIES_BULK_UPLOAD_TEMPLATE_BUTTON')}
                  </>
                )}
              </button>
            
            </div>
            {showFieldGuide && (
              <div className="alert alert-info m-t-20" role="alert">
                <h6 className="m-b-10">{t('UNIVERSITIES_BULK_UPLOAD_GUIDE_TITLE')}</h6>
                <ul className="m-b-0">
                  <li>{t('UNIVERSITIES_BULK_UPLOAD_GUIDE_NAME')}</li>
                  <li>{t('UNIVERSITIES_BULK_UPLOAD_GUIDE_CITY')}</li>
                  <li>{t('UNIVERSITIES_BULK_UPLOAD_GUIDE_TYPE')}</li>
                  <li>{t('UNIVERSITIES_BULK_UPLOAD_GUIDE_STATUS')}</li>
                </ul>
              </div>
            )}
          </div>
          <div className="col-lg-5 col-md-12 m-t-20">
            <div className="card">
              <div className="body">
                <FileUpload
                  key={bulkUploaderKey}
                  accept=".xlsx,.xls,.csv"
                  label={t('UNIVERSITIES_BULK_UPLOAD_FILE_LABEL')}
                  showPreview={false}
                  onFileSelect={handleBulkFileSelect}
                />
                <p className="text-muted small m-t-10">{getBulkStatusMessage()}</p>
                {bulkFile && (
                  <div className="alert alert-secondary m-t-15">
                    <div className="d-flex align-items-center">
                      <i className="zmdi zmdi-file-text m-r-10"></i>
                      <div>
                        <strong>{bulkFile.name}</strong>
                        <div className="small text-muted">{formatFileSize(bulkFile.size)}</div>
                      </div>
                    </div>
                  </div>
                )}
                <button
                  type="button"
                  className="btn btn-primary btn-block m-t-10"
                  style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}
                  onClick={handleBulkUpload}
                  disabled={bulkUploadStatus === 'processing' || !bulkFile}
                >
                  {bulkUploadStatus === 'processing' ? (
                    <>
                      <i className="zmdi zmdi-refresh zmdi-hc-spin m-r-5"></i>
                      {t('UNIVERSITIES_BULK_UPLOAD_PROCESSING')}
                    </>
                  ) : (
                    <>
                      <i className="zmdi zmdi-upload m-r-5"></i>
                      {t('UNIVERSITIES_BULK_UPLOAD_SUBMIT')}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (loading) {
    return (
      <section className="content">
        <div className="container-fluid">
          <div className="row clearfix">
            <div className="col-12 text-center p-5">
              <p>{t('COMMON_LOADING')}</p>
            </div>
          </div>
        </div>
      </section>
    )
  }

  return (
    <section className="content">
      <div className="block-header">
        <div className="row">
          <div className="col-lg-7 col-md-6 col-sm-12 text-left">
            <h2>
              {isEditMode ? t('UNIVERSITIES_FORM_TITLE_EDIT') : t('UNIVERSITIES_FORM_TITLE_NEW')}
              <small>{isEditMode ? formData.name || t('UNIVERSITIES_FORM_SUBTITLE_EDIT_LOADING') : t('UNIVERSITIES_FORM_SUBTITLE_NEW')}</small>
            </h2>
          </div>
          <div className="col-lg-5 col-md-6 col-sm-12">
            <button 
              className="btn btn-white btn-icon btn-round float-right m-l-10" 
              type="button"
              onClick={() => navigate('/universities')}
              title={t('UNIVERSITIES_FORM_BACK')}
            >
              <i className="zmdi zmdi-arrow-left"></i>
            </button>
            <ul className="breadcrumb float-md-right">
              <li className="breadcrumb-item">
                <Link to="/dashboard">
                  <i className="zmdi zmdi-home" style={{marginRight: '5px'}}></i> {t('COMMON_HOME')}
                </Link>
              </li>
              <li className="breadcrumb-item">
                <Link to="/universities">{t('UNIVERSITIES_BREADCRUMB_LIST')}</Link>
              </li>
              <li className="breadcrumb-item active">
                {isEditMode ? t('UNIVERSITIES_FORM_BREADCRUMB_EDIT') : t('UNIVERSITIES_FORM_BREADCRUMB_NEW')}
              </li>
            </ul>
          </div>
        </div>
      </div>

      <div className="container-fluid">
        <div className="row clearfix">
          <div className="col-lg-12">
            <div className="card">
              {/* Tab navigation - Only in create mode */}
              {!isEditMode && (
                <ul className="nav nav-tabs">
                  <li className="nav-item">
                    <a
                      href="#university-single"
                      className={`nav-link ${activeTab === 'single' ? 'active' : ''}`}
                      onClick={(e) => {
                        e.preventDefault()
                        handleTabChange('single')
                      }}
                    >
                      <i className="zmdi zmdi-plus-circle m-r-5"></i>
                      {t('UNIVERSITIES_FORM_TAB_SINGLE')}
                    </a>
                  </li>
                  <li className="nav-item">
                    <a
                      href="#university-bulk"
                      className={`nav-link ${activeTab === 'bulk' ? 'active' : ''}`}
                      onClick={(e) => {
                        e.preventDefault()
                        handleTabChange('bulk')
                      }}
                    >
                      <i className="zmdi zmdi-file-text m-r-5"></i>
                      {t('UNIVERSITIES_FORM_TAB_BULK')}
                    </a>
                  </li>
                </ul>
              )}

              <div className="tab-content">
                {isEditMode ? (
                  /* Edit Mode Layout (No Tabs) */
                  <div className="body" style={{ paddingTop: '20px' }}>
                    {renderSingleForm()}
                  </div>
                ) : (
                  /* Create Mode Layout (With Tabs) */
                  <>
                    <div
                      className={`tab-pane body ${activeTab === 'single' ? 'active' : ''}`}
                      id="university-form-single"
                      style={{ paddingTop: '20px' }}
                    >
                      {renderSingleForm()}
                    </div>
                    <div
                      className={`tab-pane body ${activeTab === 'bulk' ? 'active' : ''}`}
                      id="university-form-bulk"
                      style={{ paddingTop: '20px' }}
                    >
                      {renderBulkUpload()}
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

export default UniversityFormPage
